import React, { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import uploadService from "../../services/uploadService";

const RichTextEditor = ({ value, onChange, placeholder = "Write something..." }) => {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create Quill instance
    const imageHandler = function () {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");
      input.click();
      input.onchange = async () => {
        const file = input.files && input.files[0];
        if (!file) return;
        const range = quillRef.current.getSelection(true);
        // Optional: insert a placeholder while uploading
        try {
          const url = await uploadService.uploadImage(file);
          quillRef.current.insertEmbed(range.index, "image", url);
          quillRef.current.setSelection(range.index + 1);
        } catch (err) {
          console.error("Image upload failed:", err);
        }
      };
    };

    const tableHandler = function () {
      const range = quillRef.current.getSelection(true);
      const tableHtml =
        '<table class="w-full" style="border-collapse:collapse;border:1px solid #ddd;"><tbody><tr><td style="border:1px solid #ddd;padding:8px"></td><td style="border:1px solid #ddd;padding:8px"></td></tr><tr><td style="border:1px solid #ddd;padding:8px"></td><td style="border:1px solid #ddd;padding:8px"></td></tr></tbody></table><p><br></p>';
      quillRef.current.clipboard.dangerouslyPasteHTML(range.index, tableHtml);
      quillRef.current.setSelection(range.index + 1);
    };

    const quill = new Quill(container, {
      theme: "snow",
      placeholder,
      modules: {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image", "clean", "table"],
          ],
          handlers: {
            image: imageHandler,
            table: tableHandler,
          },
        },
      },
    });

    quillRef.current = quill;

    // Set initial value
    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value);
    }

    // Text change listener
    quill.on("text-change", () => {
      if (isUpdatingRef.current) return;
      const html = container.querySelector(".ql-editor").innerHTML;
      // If it's just empty paragraphs, send empty string
      const isEmpty = html === "<p><br></p>" || html === "";
      onChange(isEmpty ? "" : html);
    });

    return () => {
      // Clean up quill instance container if component unmounts
      if (container) {
        container.innerHTML = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update Quill content if `value` prop changes from outside
  useEffect(() => {
    if (!quillRef.current) return;
    const currentHtml = containerRef.current.querySelector(".ql-editor").innerHTML;
    if (value !== currentHtml) {
      isUpdatingRef.current = true;
      quillRef.current.clipboard.dangerouslyPasteHTML(value || "");
      isUpdatingRef.current = false;
    }
  }, [value]);

  return (
    <div className="w-full bg-white rounded-md border border-slate-200 overflow-hidden">
      <div ref={containerRef} className="min-h-[150px]" />
    </div>
  );
};

export default RichTextEditor;
